import { Select, SelectItem } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetchAccountQuery } from '@/slices/account-api-slice.ts';
import { useSecurityContext } from '../../hooks/security-provider.tsx';
import type { FormControlProps } from '../../types/form-props.type.ts';

interface MeetingLocationSelectorProps extends FormControlProps {
  accountKey?: string;
}

const MeetingLocationSelector: FC<MeetingLocationSelectorProps> = ({
  name,
  label = 'Meeting Room',
  displayProperty = 'description',
  disabled,
  required,
  accountKey,
}) => {
  const { currentAccount } = useSecurityContext();
  const { data } = useFetchAccountQuery(accountKey ?? currentAccount?.key ?? skipToken);
  const { control, setValue } = useFormContext();

  useEffect(() => {
    if (data?.meetingLocations?.length == 1) {
      setValue(name, data?.meetingLocations[0]);
    }
  }, [data?.meetingLocations, name, setValue]);

  const locations = data?.meetingLocations?.length > 0 ? data.meetingLocations : [];

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required && 'This field is required' }}
      render={({ field, fieldState }) => (
        <Select
          label={label}
          placeholder={`Select ${label}`}
          isRequired={required}
          isDisabled={disabled}
          isInvalid={fieldState.invalid}
          errorMessage={fieldState.error?.message}
          variant='bordered'
          items={locations}
          selectedKeys={field.value?.id ? new Set([field.value.id.toString()]) : new Set([])}
          onSelectionChange={keys => {
            const selectedId = Array.from(keys)[0];
            if (selectedId) {
              const selectedLocation = data?.meetingLocations?.find(
                x => x.id === parseInt(selectedId as string),
              );
              field.onChange(selectedLocation);
            } else {
              field.onChange(null);
            }
          }}
        >
          {item => (
            <SelectItem key={item.id.toString()} textValue={item[displayProperty]}>
              {item[displayProperty]}
            </SelectItem>
          )}
        </Select>
      )}
    />
  );
};

export default MeetingLocationSelector;
